import { activeAnalyticsClient } from './active-client';
import type { AnalyticsEventName, PropsFor } from './events';
import type { AnalyticsPersonProps, AnalyticsProperties, FeatureFlagValue, SurveySubmission } from './types';
import { subscriptionSummary, type SubscriptionInput } from './subscription-value';

// ==============================|| ANALYTICS — DOMAIN FUNCTIONS (LAYER 3) ||============================== //
// The typed API call sites import — never posthog-js, the adapter, or an event
// name string. Plain functions (not hooks) so they work in event handlers,
// async callbacks and services alike.

function track<N extends AnalyticsEventName>(name: N, props: PropsFor<N>): void {
  activeAnalyticsClient.capture(name, props as AnalyticsProperties);
}

// The ask's 1st–3rd dismiss, each kept as its own person property so the flag can count them.
const EA_FEEDBACK_DISMISSALS = ['ea_feedback_dismissed_1', 'ea_feedback_dismissed_2', 'ea_feedback_dismissed_3'] as const;

// Billing refetches every 30s and after every mutation; the person's value
// rarely changes, so an unchanged summary is not re-sent.
let lastSubscriptionSummary = '';

export const analytics = {
  // ── Lifecycle ──────────────────────────────────────────────────────────────
  // Called once at boot (index.tsx), before React renders.
  init: (): void => activeAnalyticsClient.init(),
  isReady: (): boolean => activeAnalyticsClient.isReady(),

  // ── Feature flags (experiments) — see utils/experiment.ts for the rules ────
  onFeatureFlags: (cb: () => void): void => activeAnalyticsClient.onFeatureFlags(cb),
  getFeatureFlag: (key: string, sendEvent?: boolean): FeatureFlagValue => activeAnalyticsClient.getFeatureFlag(key, sendEvent),
  reloadFeatureFlags: (): void => activeAnalyticsClient.reloadFeatureFlags(),

  // ── Surveys drawn by our own UI (the cancellation reason) ──────────────────
  surveyShown: (surveyId: string): void => activeAnalyticsClient.surveyShown(surveyId),
  surveyDismissed: (surveyId: string): void => activeAnalyticsClient.surveyDismissed(surveyId),
  surveySent: (submission: SurveySubmission): void => activeAnalyticsClient.surveySent(submission),

  // ── Early-access feedback: person properties the `ea-feedback-ask` flag reads back ──
  eaFeedbackAnswered: (): void => activeAnalyticsClient.setPersonProperties({ ea_feedback_answered_at: new Date().toISOString() }),
  // `ask` is which ask (1–3) was dismissed; anything else records nothing.
  eaFeedbackDismissed: (ask: number): void => {
    const property = EA_FEEDBACK_DISMISSALS[ask - 1];
    if (property) activeAnalyticsClient.setPersonProperties({ [property]: new Date().toISOString() });
  },

  // ── Identity ───────────────────────────────────────────────────────────────
  identifyUser: (distinctId: string, traits?: AnalyticsPersonProps): void => activeAnalyticsClient.identify(distinctId, traits),
  // Refreshes the person's current value whenever billing loads. Person
  // properties, not a re-identify: the billing store knows the internal user id,
  // never the Auth0 subject that is the distinct_id. Takes the raw subscriptions
  // and fails silently, so the store can never be broken by analytics.
  setSubscriptionSummary: (subscriptions: SubscriptionInput[] | null | undefined): void => {
    try {
      const summary = subscriptionSummary(subscriptions);
      const key = JSON.stringify(summary);
      if (key === lastSubscriptionSummary) return;
      lastSubscriptionSummary = key;
      activeAnalyticsClient.setPersonProperties(summary);
    } catch {
      /* analytics never throws */
    }
  },
  reset: (): void => {
    lastSubscriptionSummary = '';
    activeAnalyticsClient.reset();
  },

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  userSignedUp: (): void => track('user:signed_up', undefined),

  // ── Account connection (broker/platform metrics) ───────────────────────────
  accountConnected: (p: PropsFor<'account:connected'>): void => track('account:connected', p),

  // ── Billing (the moments the server-side snapshot cannot observe) ──────────
  cancelIntended: (p: PropsFor<'subscription:cancel_intended'>): void => track('subscription:cancel_intended', p),
  cancelAborted: (p: PropsFor<'subscription:cancel_aborted'>): void => track('subscription:cancel_aborted', p),
  subscriptionCanceled: (p: PropsFor<'subscription:canceled'>): void => track('subscription:canceled', p),
  subscriptionResumed: (p: PropsFor<'subscription:resumed'>): void => track('subscription:resumed', p),

  // ── Subscription freeze ────────────────────────────────────────────────────
  freezePreviewed: (p: PropsFor<'subscription:freeze_previewed'>): void => track('subscription:freeze_previewed', p),
  subscriptionFrozen: (p: PropsFor<'subscription:frozen'>): void => track('subscription:frozen', p),
  freezeResumed: (p: PropsFor<'subscription:freeze_resumed'>): void => track('subscription:freeze_resumed', p),
  freezeCanceled: (p: PropsFor<'subscription:freeze_canceled'>): void => track('subscription:freeze_canceled', p),

  // ── Leaving (the cancellation dialog) ──────────────────────────────────────
  leaveStarted: (p: PropsFor<'leave:started'>): void => track('leave:started', p),
  leaveCompleted: (p: PropsFor<'leave:completed'>): void => track('leave:completed', p),
  leaveAbandoned: (p: PropsFor<'leave:abandoned'>): void => track('leave:abandoned', p),
  leavePaused: (p: PropsFor<'leave:paused'>): void => track('leave:paused', p),
  leaveOfferShown: (p: PropsFor<'leave:offer_shown'>): void => track('leave:offer_shown', p),
  leaveOfferAccepted: (p: PropsFor<'leave:offer_accepted'>): void => track('leave:offer_accepted', p),

  // ── Onboarding: get-started, plan gate, welcome tour ───────────────────────
  getStartedViewed: (p: PropsFor<'get_started_viewed'>): void => track('get_started_viewed', p),
  getStartedCtaClicked: (p: PropsFor<'get_started_cta_clicked'>): void => track('get_started_cta_clicked', p),
  planBannerViewed: (p: PropsFor<'plan_banner_viewed'>): void => track('plan_banner_viewed', p),
  quickSubscribeViewed: (p: PropsFor<'quick_subscribe_viewed'>): void => track('quick_subscribe_viewed', p),
  quickSubscribeDismissed: (p: PropsFor<'quick_subscribe_dismissed'>): void => track('quick_subscribe_dismissed', p),
  tourStarted: (): void => track('tour_started', undefined),
  tourStepViewed: (p: PropsFor<'tour_step_viewed'>): void => track('tour_step_viewed', p),
  tourStepCompleted: (p: PropsFor<'tour_step_completed'>): void => track('tour_step_completed', p),
  tourCompleted: (): void => track('tour_completed', undefined),
  tourSkipped: (p: PropsFor<'tour_skipped'>): void => track('tour_skipped', p),
  purchase: (p: PropsFor<'purchase'>): void => track('purchase', p),

  // ── Performance certificate ────────────────────────────────────────────────
  certificateGenerated: (p: PropsFor<'certificate:generated'>): void => track('certificate:generated', p),
  certificateShared: (p: PropsFor<'certificate:shared'>): void => track('certificate:shared', p),

  // ── What's New ─────────────────────────────────────────────────────────────
  announcementShown: (p: PropsFor<'announcement:shown'>): void => track('announcement:shown', p),
  announcementClicked: (p: PropsFor<'announcement:clicked'>): void => track('announcement:clicked', p),
  announcementDismissed: (p: PropsFor<'announcement:dismissed'>): void => track('announcement:dismissed', p),
  whatsNewOpened: (p: PropsFor<'whats_new:opened'>): void => track('whats_new:opened', p)
};
